import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { green, purple, red, universalStyles, white } from '../../../../app/theme';
import { ChildNodeProps } from '../../../../constants/learningCheck.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  questionRow: {
    paddingTop: 23,
    display: 'flex',
  },
  number: {
    color: white,
    paddingRight: 15,
    fontWeight: 'bold',
  },
  question: {
    fontSize: 20,
    color: white,
    paddingBottom: 8,
  },
  title: {
    color: white,
    fontSize: 22,
    fontWeight: 'bold',
  },
  table: {
    marginTop: 10,
    border: `2px solid ${white}`,
    borderCollapse: 'collapse',
    width: '100%',
  },
  header: {
    backgroundColor: purple,
    textAlign: 'center',
    '& > tr > td': {
      width: '50%',
      border: `1px solid ${white}`,
      padding: 8,
    },
  },
  body: {
    '& > tr > td': {
      width: '50%',
      border: `1px solid ${white}`,
      backgroundColor: green,
      padding: '10px 25px',
      '&:last-of-type': {
        backgroundColor: red,
      },
    },
  },
  cell: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    color: white,
    fontSize: 50,
  },
  answersWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-around',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  answer: {
    backgroundColor: white,
    width: 350,
    padding: '0 5px',
    margin: 20,
  },
  answerSpot: {
    width: 'calc(100% - 75px)',
    marginLeft: 10,
    minHeight: 60,
  },
}));

const AnswerSpot = ({ id, list }: { id: string; list: string[] }): JSX.Element => {
  const classes = useStyles();

  return (
    <Droppable droppableId={id} isDropDisabled={!!list.length}>
      {(provided) => (
        <div className={classes.answerSpot} ref={provided.innerRef} {...provided.droppableProps}>
          {list.map((answer, index) => (
            <Draggable draggableId={answer} index={index} key={answer}>
              {(nestedProvided) => (
                <div
                  className={classes.answer}
                  {...nestedProvided.draggableProps}
                  {...nestedProvided.dragHandleProps}
                  ref={nestedProvided.innerRef}
                >
                  <Typography variant="body1">{answer}</Typography>
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

const Acquisition07 = ({ setSelectedAnswer }: ChildNodeProps): JSX.Element => {
  const classes = useStyles();
  const [originList, setOriginList] = useState([
    'Should patients lift their arms before administration is complete?',
    'After administration should the patient be in the supine position with arms above their head?',
    'Should a volume of undiluted Axumin that is greater than 5 mL be administered to the patient?',
    'Should technologists administer 370 MBq (10 mCi) as an intravenous bolus injection while patient is positioned supine in PET/CT scanner?',
    'During administration should the patient’s arms be adjacent to the torso (arms down)?',
    'Should PET scanning begin before 3 to 5 minutes after administration of Axumin?',
  ]);
  const [rec1, setRec1] = useState<string[]>([]);
  const [rec2, setRec2] = useState<string[]>([]);
  const [rec3, setRec3] = useState<string[]>([]);
  const [notRec1, setNotRec1] = useState<string[]>([]);
  const [notRec2, setNotRec2] = useState<string[]>([]);
  const [notRec3, setNotRec3] = useState<string[]>([]);

  const getList = (id: string | undefined) => {
    let list: string[];
    let setList;
    switch (id) {
      case '0':
        list = originList;
        setList = (newList: string[]) => setOriginList(newList);
        break;
      case '1':
        list = rec1;
        setList = (newList: string[]) => setRec1(newList);
        break;
      case '2':
        list = notRec1;
        setList = (newList: string[]) => setNotRec1(newList);
        break;
      case '3':
        list = rec2;
        setList = (newList: string[]) => setRec2(newList);
        break;
      case '4':
        list = notRec2;
        setList = (newList: string[]) => setNotRec2(newList);
        break;
      case '5':
        list = rec3;
        setList = (newList: string[]) => setRec3(newList);
        break;
      case '6':
        list = notRec3;
        setList = (newList: string[]) => setNotRec3(newList);
        break;
      default:
        list = [];
        break;
    }
    return { list, setList };
  };

  const onDragEnd = (result: DropResult) => {
    const moved = result.draggableId;
    const { list, setList } = getList(result.destination?.droppableId);
    const source = getList(result.source.droppableId);
    if (list && setList && source.list && source.setList && (list === originList || list.length === 0)) {
      const tempSource = [...source.list];
      const tempDestination = [...list];
      tempSource.splice(result.source.index, 1);
      source.setList(tempSource);
      tempDestination.push(moved);
      setList(tempDestination);

      let origin = originList;
      if (source.list === originList) {
        origin = tempSource;
      } else if (list === originList) {
        origin = tempDestination;
      }
      setSelectedAnswer(!origin.length);
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className={classes.grayBox}>
          <Typography variant="h6">Drag and drop</Typography>
          <div className={classes.questionRow}>
            <Typography variant="body2" className={classes.number}>
              7.
            </Typography>
            <div>
              <Typography variant="body2" className={classes.question}>
                Drag the appropriate responses to the corresponding column.
              </Typography>
              <Typography variant="body2" className={classes.question}>
                What is recommended and what is not recommended?
              </Typography>
            </div>
          </div>
          <table className={classes.table}>
            <thead className={classes.header}>
              <tr>
                <td>
                  <Typography className={classes.title}>Recommended</Typography>
                </td>
                <td>
                  <Typography className={classes.title}>Not Recommended</Typography>
                </td>
              </tr>
            </thead>
            <tbody className={classes.body}>
              <tr>
                <td>
                  <div className={classes.cell}>
                    <CheckCircleIcon className={classes.icon} />
                    <Droppable droppableId="1" isDropDisabled={!!rec1.length}>
                      {(provided) => (
                        <div className={classes.answerSpot} ref={provided.innerRef} {...provided.droppableProps}>
                          {rec1.map((answer, index) => (
                            <Draggable draggableId={answer} index={index} key={answer}>
                              {(nestedProvided) => (
                                <div
                                  className={classes.answer}
                                  {...nestedProvided.draggableProps}
                                  {...nestedProvided.dragHandleProps}
                                  ref={nestedProvided.innerRef}
                                >
                                  <Typography variant="body1">{answer}</Typography>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </td>
                <td>
                  <div className={classes.cell}>
                    <CancelIcon className={classes.icon} />
                    <AnswerSpot id="2" list={notRec1} />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className={classes.cell}>
                    <CheckCircleIcon className={classes.icon} />
                    <AnswerSpot id="3" list={rec2} />
                  </div>
                </td>
                <td>
                  <div className={classes.cell}>
                    <CancelIcon className={classes.icon} />
                    <AnswerSpot id="4" list={notRec2} />
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className={classes.cell}>
                    <CheckCircleIcon className={classes.icon} />
                    <AnswerSpot id="5" list={rec3} />
                  </div>
                </td>
                <td>
                  <div className={classes.cell}>
                    <CancelIcon className={classes.icon} />
                    <AnswerSpot id="6" list={notRec3} />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Droppable droppableId="0" direction="horizontal">
          {(provided) => (
            <div className={classes.answersWrapper} ref={provided.innerRef} {...provided.droppableProps}>
              {originList.map((answer, index) => (
                <Draggable draggableId={answer} index={index} key={answer}>
                  {(nestedProvided) => (
                    <div
                      className={classes.answer}
                      {...nestedProvided.draggableProps}
                      {...nestedProvided.dragHandleProps}
                      ref={nestedProvided.innerRef}
                    >
                      <Typography variant="body1">{answer}</Typography>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default Acquisition07;
